"use client";

import { useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import posthog from "posthog-js";
import { AlertTriangle } from "lucide-react";

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // posthog-js's capture_exceptions already captures the underlying error as $exception
    // (which fires the Slack alert). This explicit event lets us build a "users who saw
    // the error page" insight separately from raw exception counts.
    posthog.capture("page_error_shown", {
      message: error.message,
      digest: error.digest,
    });
  }, [error]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-white px-6">
      <Link href="/" className="mb-12">
        <Image
          src="/funnlr_logo.png"
          alt="Funnlr"
          width={120}
          height={32}
          className="h-8 w-auto"
          priority
        />
      </Link>

      <div className="mx-auto max-w-md text-center">
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-red-50">
          <AlertTriangle className="h-8 w-8 text-red-500" />
        </div>

        <h1 className="font-heading text-2xl font-bold text-slate-900">
          Something went wrong
        </h1>
        <p className="mt-3 text-sm leading-relaxed text-slate-500">
          We hit an unexpected error on our end. Our team&apos;s been notified
          and is looking into it.
        </p>

        <div className="mt-8 flex flex-col items-center gap-3">
          <button
            onClick={reset}
            className="rounded-full bg-brand-500 px-8 py-3 text-sm font-semibold text-white transition-colors hover:bg-brand-600"
          >
            Try again
          </button>
          <Link href="/" className="text-sm text-slate-500 hover:text-slate-700">
            Back to home
          </Link>
        </div>

        {error.digest ? (
          <p className="mt-8 text-xs text-slate-300">
            Reference: {error.digest}
          </p>
        ) : null}
      </div>
    </div>
  );
}
